import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { AssetPreview } from "../../components/AssetPreview";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "../../lib/api";
import { useTenantAuth } from "../../context/auth";

type BrandingResponse = {
  tenant_id: string;
  draft: { theme: any; content: any; layout: any; updated_at?: string | null; updated_by_user_id?: string | null };
  published: { version?: number | null; snapshot?: any };
};

type Asset = {
  id: string;
  type: string;
  title?: string | null;
  original_filename: string;
  alt_text?: string | null;
  is_decorative?: boolean;
  preview_url?: string | null;
  deleted_at?: string | null;
};

const CORE_SECTION_TYPES = new Set(["services", "dateTime", "confirm"]);
const BLOCK_SCHEMAS: Record<
  string,
  {
    label: string;
    variants: string[];
    defaultVariant?: string;
    canDelete?: boolean;
    canInlineEdit?: boolean;
    contentFields?: Array<{ key: "header.title" | "header.subtitle" | "policy.text" | "footer.text"; label: string; multiline?: boolean }>;
  }
> = {
  header: {
    label: "Hero",
    variants: ["hero", "compact"],
    defaultVariant: "hero",
    canInlineEdit: true,
    contentFields: [
      { key: "header.title", label: "Hero title" },
      { key: "header.subtitle", label: "Hero subtitle", multiline: true },
    ],
  },
  howItWorks: { label: "How It Works", variants: ["timeline", "cards"] },
  services: { label: "Services", variants: ["list", "grid", "cardsWithImages"], defaultVariant: "grid", canDelete: false },
  staff: { label: "Staff", variants: ["pills", "cardsWithPhotos"], defaultVariant: "pills" },
  dateTime: { label: "Date & Time", variants: ["chips"], defaultVariant: "chips", canDelete: false },
  confirm: { label: "Confirm", variants: ["summary"], defaultVariant: "summary", canDelete: false },
  policies: {
    label: "Policies",
    variants: ["accordion"],
    defaultVariant: "accordion",
    contentFields: [{ key: "policy.text", label: "Before you arrive", multiline: true }],
  },
  footer: {
    label: "Footer",
    variants: ["minimal"],
    defaultVariant: "minimal",
    contentFields: [{ key: "footer.text", label: "Footer text", multiline: true }],
  },
  announcement: { label: "Announcement", variants: ["banner"], defaultVariant: "banner" },
  gallery: { label: "Gallery", variants: ["masonry", "slider"], defaultVariant: "masonry" },
  reviews: { label: "Reviews", variants: ["carousel", "grid"], defaultVariant: "carousel" },
  faq: { label: "FAQ", variants: ["accordion"], defaultVariant: "accordion" },
  mapContact: { label: "Map & Contact", variants: ["split"], defaultVariant: "split" },
};
const SECTION_VARIANTS: Record<string, string[]> = Object.fromEntries(
  Object.entries(BLOCK_SCHEMAS).map(([k, v]) => [k, v.variants])
);

type BrandingTemplate = {
  id: string;
  name: string;
  category: string;
  description?: string;
  preview_title?: string;
  snapshot: any;
};

type BrandingV2Project = {
  id: string;
  name: string;
  active_draft_revision_id?: string | null;
  active_published_revision_id?: string | null;
  default_locale?: string;
};

type BrandingV2Lock = {
  project_id: string;
  lock_token: string;
  expires_at: string;
};

type BrandingV2Revision = {
  id: string;
  etag?: string | null;
  content_json: any;
  design_tokens_json: any;
  pages_json: any;
  booking_theme_json: any;
  asset_bindings_json: any;
  validation_report_json?: { ok?: boolean; errors?: Array<{ code: string; path: string; message: string }>; warnings?: Array<{ code: string; path: string; message: string }> } | null;
};

type BrandingV2ValidationReport = {
  ok: boolean;
  errors: Array<{ code: string; path: string; message: string }>;
  warnings: Array<{ code: string; path: string; message: string }>;
};

type BrandingV2Diff = {
  changed_paths: string[];
  summary?: { changed_count?: number };
};

type BrandingPreviewLink = {
  preview_token: string;
  preview_url: string;
  expires_at: string;
};
const SIMPLE_TABS = [
  { id: "brandKit", label: "Style" },
  { id: "content", label: "Content" },
  { id: "media", label: "Media" },
  { id: "publish", label: "Publish" },
] as const;

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function snapshotToV2Patch(snapshot: any) {
  const sections = Array.isArray(snapshot?.layout?.sections) ? snapshot.layout.sections : [];
  const header = snapshot?.theme?.components?.header || {};
  const assetBindings: Record<string, unknown> = {};
  if (header.heroImageAssetId) {
    assetBindings["hero.main"] = { asset_id: String(header.heroImageAssetId) };
  }
  if (Array.isArray(header.auxiliaryImageAssetIds) && header.auxiliaryImageAssetIds.length > 0) {
    assetBindings["hero.aux"] = header.auxiliaryImageAssetIds.filter(Boolean).map((id: string) => ({ asset_id: String(id) }));
  }
  return {
    content_json: snapshot?.content || {},
    design_tokens_json: snapshot?.theme?.tokens || {},
    booking_theme_json: snapshot?.theme?.components || {},
    pages_json: {
      pages: [
        {
          id: "booking",
          title: "Booking",
          blocks: sections.map((section: any, idx: number) => ({
            id: String(section?.id || `block-${idx + 1}`),
            type: String(section?.type || "Section"),
            enabled: Boolean(section?.enabled ?? true),
            props: {
              ...(section?.variant ? { variant: section.variant } : {}),
              ...(section?.hiddenOnMobile ? { hiddenOnMobile: true } : {}),
            },
          })),
        },
      ],
    },
    asset_bindings_json: assetBindings,
  };
}

function defaultSnapshot() {
  return {
    schemaVersion: 1,
    theme: {
      tokens: {
        colors: {
          primary: "#0f766e",
          secondary: "#0f172a",
          accent: "#f59e0b",
          background: "#f8fafc",
          surface: "#ffffff",
          textPrimary: "#0f172a",
          textSecondary: "#475569",
          border: "#dbe3e7",
          muted: "#e2e8f0",
          success: "#16a34a",
          warning: "#d97706",
          danger: "#dc2626",
        },
        typography: {
          fontHeading: { family: "'Space Grotesk', sans-serif", weight: 700 },
          fontBody: { family: "'IBM Plex Sans', sans-serif", weight: 400 },
          baseSizePx: 16,
          lineHeight: "normal",
          buttonCasing: "normal",
        },
        shape: { radius: { xs: 4, sm: 8, md: 12, lg: 18, pill: 999 }, shadow: "subtle" },
        density: "comfortable",
      },
      components: {
        header: { variant: "hero", alignment: "left", paddingY: "lg", overlay: { color: "#000000", opacity: 0.35 }, auxiliaryImageAssetIds: [] },
        buttons: { primary: { variant: "filled", radius: "md" } },
        serviceCard: { variant: "grid", showDurationBadge: true, showPrice: true, imageMode: "cover" },
        staffSelector: { variant: "pills", showPhotos: false },
        slotChip: { variant: "outline", size: "md" },
        stepper: { variant: "numbers", showLabels: true },
        form: { inputVariant: "outline", radius: "md" },
        section: { surfaceVariant: "surface" },
      },
    },
    layout: {
      sections: [
        { id: "header", type: "header", enabled: true, variant: "hero" },
        { id: "how-it-works", type: "howItWorks", enabled: true },
        { id: "services", type: "services", enabled: true, variant: "grid" },
        { id: "staff", type: "staff", enabled: true, variant: "pills" },
        { id: "date-time", type: "dateTime", enabled: true, variant: "chips" },
        { id: "confirm", type: "confirm", enabled: true },
        { id: "policies", type: "policies", enabled: true, variant: "accordion" },
        { id: "footer", type: "footer", enabled: true, variant: "minimal" },
      ],
    },
    content: {
      header: { title: "Booking", subtitle: "Choose service, specialist and time." },
      policies: { beforeYouArrive: "Please arrive 5 minutes before your appointment." },
      footer: { enabled: true, text: "Thank you for booking with us.", links: [] },
      i18n: {
        locales: ["en"],
        strings: {
          "hero.title": { en: "Booking" },
          "hero.subtitle": { en: "Choose service, specialist and time." },
          "policy.text": { en: "Please arrive 5 minutes before your appointment." },
          "footer.text": { en: "Thank you for booking with us." },
        },
      },
    },
  };
}

function coerceSnapshot(res?: BrandingResponse | null) {
  const base = defaultSnapshot();
  const theme = res?.draft?.theme;
  const content = res?.draft?.content;
  const layout = res?.draft?.layout;
  if (theme?.tokens && content?.header && layout?.sections) {
    return {
      schemaVersion: 1,
      theme: {
        ...theme,
        components: {
          ...theme.components,
          header: {
            ...(theme.components?.header || {}),
            auxiliaryImageAssetIds: Array.isArray(theme.components?.header?.auxiliaryImageAssetIds)
              ? theme.components.header.auxiliaryImageAssetIds
              : [],
          },
        },
      },
      content,
      layout,
    };
  }
  return base;
}

function getContentPathValue(content: any, key: string): string {
  if (key === "header.title") return String(content?.header?.title || "");
  if (key === "header.subtitle") return String(content?.header?.subtitle || "");
  if (key === "policy.text") return String(content?.policies?.beforeYouArrive || "");
  if (key === "footer.text") return String(content?.footer?.text || "");
  return "";
}

function setContentPathValue(content: any, key: string, value: string) {
  if (key === "header.title") return { ...content, header: { ...(content?.header || {}), title: value } };
  if (key === "header.subtitle") return { ...content, header: { ...(content?.header || {}), subtitle: value } };
  if (key === "policy.text") return { ...content, policies: { ...(content?.policies || {}), beforeYouArrive: value } };
  if (key === "footer.text") return { ...content, footer: { ...(content?.footer || {}), text: value } };
  return content;
}

function getLocalizedContentValue(snapshot: any, key: string, locale: string, fallbackLocale = "en"): string {
  const strings = snapshot?.content?.i18n?.strings || snapshot?.content?.strings || {};
  const entry = strings?.[key];
  if (entry && typeof entry === "object") {
    const localized = entry[locale];
    if (typeof localized === "string") return localized;
    const fallback = entry[fallbackLocale];
    if (typeof fallback === "string") return fallback;
  }
  return getContentPathValue(snapshot?.content || {}, key);
}

function upsertLocalizedContent(snapshot: any, key: string, locale: string, value: string, defaultLocale = "en") {
  const content = snapshot?.content || {};
  const i18n = typeof content.i18n === "object" && content.i18n ? content.i18n : {};
  const strings = typeof i18n.strings === "object" && i18n.strings ? i18n.strings : {};
  const locales = Array.isArray(i18n.locales) ? [...new Set(i18n.locales.map((x: unknown) => String(x)))] : [];
  if (locale && !locales.includes(locale)) locales.push(locale);
  if (defaultLocale && !locales.includes(defaultLocale)) locales.unshift(defaultLocale);
  const nextContent = {
    ...content,
    i18n: {
      ...i18n,
      locales,
      strings: {
        ...strings,
        [key]: {
          ...(strings[key] || {}),
          [locale]: value,
        },
      },
    },
  };
  if (locale === defaultLocale) {
    return setContentPathValue(nextContent, key, value);
  }
  return nextContent;
}

function contrastRatio(a: string, b: string) {
  const normalize = (hex: string) => {
    const x = hex.replace("#", "");
    if (x.length !== 3 && x.length !== 6) return null;
    const y = x.length === 3 ? x.split("").map((c) => c + c).join("") : x;
    const n = parseInt(y, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255] as const;
  };
  const aa = normalize(a);
  const bb = normalize(b);
  if (!aa || !bb) return 21;
  const luminance = ([r, g, b]: readonly [number, number, number]) => {
    const s = [r, g, b].map((v) => {
      const c = v / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * s[0] + 0.7152 * s[1] + 0.0722 * s[2];
  };
  const l1 = luminance(aa);
  const l2 = luminance(bb);
  const [bright, dark] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (bright + 0.05) / (dark + 0.05);
}

export function BrandingStudioPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { token } = useTenantAuth();
  const [snapshot, setSnapshot] = useState<any>(defaultSnapshot());
  const [baseline, setBaseline] = useState<string>("");
  const [panel, setPanel] = useState<"brandKit" | "layout" | "media" | "content" | "publish" | "advanced">("brandKit");
  const [selectedSectionId, setSelectedSectionId] = useState("header");
  const [selectedPageId, setSelectedPageId] = useState("booking");
  const [device, setDevice] = useState<"desktop" | "tablet" | "mobile">("desktop");
  const [previewMode, setPreviewMode] = useState<"draft" | "published">("draft");
  const [publishNotes, setPublishNotes] = useState("");
  const [assetFilter, setAssetFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [draggingSectionId, setDraggingSectionId] = useState<string | null>(null);
  const [newBlockType, setNewBlockType] = useState<string>("announcement");
  const [contentLocale, setContentLocale] = useState<string>("en");
  const [historyStack, setHistoryStack] = useState<any[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showBrandWizard, setShowBrandWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardVibe, setWizardVibe] = useState("Minimal");
  const [wizardPrimary, setWizardPrimary] = useState("#0f766e");
  const [wizardAccent, setWizardAccent] = useState("#f59e0b");
  const [wizardHeadingFont, setWizardHeadingFont] = useState("'Space Grotesk', sans-serif");
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [lockState, setLockState] = useState<BrandingV2Lock | null>(null);
  const [draftEtag, setDraftEtag] = useState<string | null>(null);
  const [serverValidation, setServerValidation] = useState<BrandingV2ValidationReport | null>(null);

  const persistDraftSnapshot = (nextSnapshot: any = snapshot) =>
    (async () => {
      const project = projects.data?.items?.[0];
      const draftRevisionId = project?.active_draft_revision_id;
      if (project?.id && draftRevisionId && lockState?.lock_token && draftEtag) {
        const updated = await apiPatch<BrandingV2Revision>(
          `/v3/branding/revisions/${encodeURIComponent(draftRevisionId)}`,
          snapshotToV2Patch(nextSnapshot),
          {
            headers: {
              "If-Match": draftEtag,
              "X-Brand-Lock-Token": lockState.lock_token,
            },
          }
        );
        setDraftEtag(updated.etag || null);
        return updated;
      }
      return apiPut("/v3/branding/draft", {
        theme: nextSnapshot.theme,
        content: nextSnapshot.content,
        layout: nextSnapshot.layout,
      });
    })();

  const branding = useQuery({
    queryKey: ["manager-branding"],
    queryFn: () => apiGet<BrandingResponse>("/v3/branding"),
  });
  const versions = useQuery({
    queryKey: ["manager-branding-versions"],
    queryFn: () => apiGet<{ items: Array<{ version: number; created_at: string; notes?: string | null; snapshot?: any }> }>("/v3/branding/versions"),
  });
  const projects = useQuery({
    queryKey: ["manager-branding-projects"],
    queryFn: () => apiGet<{ items: BrandingV2Project[] }>("/v3/branding/projects"),
  });
  const activeProject = projects.data?.items?.[0];
  const draftRevisionId = activeProject?.active_draft_revision_id || (branding.data ? `draft:${branding.data.tenant_id}:default` : null);
  const draftRevision = useQuery({
    queryKey: ["manager-branding-draft-revision", draftRevisionId],
    queryFn: () => apiGet<BrandingV2Revision>(`/v3/branding/revisions/${encodeURIComponent(String(draftRevisionId))}`),
    enabled: !!draftRevisionId,
  });
  const publishedRevisionId = activeProject?.active_published_revision_id || (branding.data?.published?.version ? `published:v${branding.data.published.version}` : null);
  const v2Diff = useQuery({
    queryKey: ["manager-branding-diff", draftRevisionId, publishedRevisionId, baseline, panel],
    queryFn: () =>
      apiGet<BrandingV2Diff>(
        `/v3/branding/revisions/${encodeURIComponent(String(draftRevisionId))}/diff?against=${encodeURIComponent(String(publishedRevisionId))}`
      ),
    enabled: panel === "publish" && !!draftRevisionId && !!publishedRevisionId,
    staleTime: 0,
  });
  const templates = useQuery({
    queryKey: ["manager-branding-templates"],
    queryFn: () => apiGet<{ items: BrandingTemplate[] }>("/v3/branding/templates"),
  });
  const assets = useQuery({
    queryKey: ["manager-branding-assets", assetFilter, search],
    queryFn: () =>
      apiGet<{ items: Asset[] }>(
        `/v3/branding/assets?deleted=false${assetFilter !== "all" ? `&type=${encodeURIComponent(assetFilter)}` : ""}${search ? `&q=${encodeURIComponent(search)}` : ""}`
      ),
  });

  const lockProject = useMutation({
    mutationFn: (projectId: string) => apiPost<BrandingV2Lock>(`/v3/branding/projects/${encodeURIComponent(projectId)}/lock`, {}),
    onSuccess: (data) => setLockState(data),
  });

  useEffect(() => {
    if (!activeProject?.id) return;
    if (lockProject.isPending) return;
    const expiresAt = lockState?.expires_at ? new Date(lockState.expires_at).getTime() : 0;
    const needsLock = !lockState || !lockState.lock_token || expiresAt - Date.now() < 60_000;
    if (needsLock) {
      lockProject.mutate(activeProject.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id, lockState?.expires_at]);

  useEffect(() => {
    if (!branding.data) return;
    const next = coerceSnapshot(branding.data);
    setSnapshot(next);
    setBaseline(JSON.stringify(next));
    setHistoryStack([clone(next)]);
    setHistoryIndex(0);
  }, [branding.data]);

  useEffect(() => {
    const defaultLocale = activeProject?.default_locale || "en";
    setContentLocale((prev) => (prev && prev !== "en" ? prev : defaultLocale));
  }, [activeProject?.default_locale]);

  useEffect(() => {
    if (!draftRevision.data?.etag) return;
    setDraftEtag(draftRevision.data.etag);
    if (draftRevision.data.validation_report_json && typeof draftRevision.data.validation_report_json === "object") {
      setServerValidation(draftRevision.data.validation_report_json as BrandingV2ValidationReport);
    }
  }, [draftRevision.data?.etag, draftRevision.data?.validation_report_json]);

  const commitSnapshot = (updater: any) => {
    setSnapshot((prev: any) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (!next) return prev;
      const nextJson = JSON.stringify(next);
      const prevJson = JSON.stringify(prev);
      if (nextJson === prevJson) return prev;
      setHistoryStack((stack) => {
        const head = historyIndex >= 0 ? stack.slice(0, historyIndex + 1) : [];
        const merged = [...head, clone(next)];
        return merged.slice(-40);
      });
      setHistoryIndex((idx) => {
        const nextIdxBase = idx >= 0 ? idx : 0;
        return Math.min(nextIdxBase + 1, 39);
      });
      return next;
    });
  };

  const applyTemplate = (templateId: string) => {
    const selected = (templates.data?.items ?? []).find((x) => x.id === templateId);
    if (!selected?.snapshot) return;
    const next = coerceSnapshot({ draft: selected.snapshot, published: branding.data?.published, tenant_id: branding.data?.tenant_id } as any);
    commitSnapshot(next);
    setPanel("content");
  };

  const saveDraft = useMutation({
    mutationFn: () => persistDraftSnapshot(),
    onSuccess: async () => {
      setBaseline(JSON.stringify(snapshot));
      if (draftRevisionId) {
        await qc.invalidateQueries({ queryKey: ["manager-branding-draft-revision", draftRevisionId] });
      }
      await qc.invalidateQueries({ queryKey: ["manager-branding"] });
    },
  });

  const publish = useMutation({
    mutationFn: async () => {
      if (baseline && JSON.stringify(snapshot) !== baseline) {
        await persistDraftSnapshot();
      }
      setServerValidation(null);
      if (draftRevisionId && lockState?.lock_token) {
        const report = await apiPost<BrandingV2ValidationReport>(`/v3/branding/revisions/${encodeURIComponent(draftRevisionId)}/validate`, {});
        setServerValidation(report);
        if (!report.ok) {
          throw new Error(`branding_invalid: ${report.errors[0]?.message || "Validation failed"}`);
        }
        return apiPost<{ version: number }>(
          `/v3/branding/revisions/${encodeURIComponent(draftRevisionId)}/publish`,
          { notes: publishNotes || undefined },
          { headers: { "X-Brand-Lock-Token": lockState.lock_token } }
        );
      }
      return apiPost<{ version: number }>("/v3/branding/publish", { notes: publishNotes || undefined });
    },
    onSuccess: async () => {
      setPublishNotes("");
      setBaseline(JSON.stringify(snapshot));
      if (draftRevisionId) {
        await qc.invalidateQueries({ queryKey: ["manager-branding-draft-revision", draftRevisionId] });
      }
      await qc.invalidateQueries({ queryKey: ["manager-branding"] });
      await qc.invalidateQueries({ queryKey: ["manager-branding-versions"] });
      await qc.invalidateQueries({ queryKey: ["manager-branding-projects"] });
    },
  });

  const rollback = useMutation({
    mutationFn: (version: number) => apiPost(`/v3/branding/rollback/${version}`, {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["manager-branding"] });
      await qc.invalidateQueries({ queryKey: ["manager-branding-versions"] });
    },
  });

  const uploadAsset = async (file: File, type: string) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("type", type);
    const res = await fetch("/v3/branding/assets", { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
    if (!res.ok) throw new Error("Asset upload failed");
    await qc.invalidateQueries({ queryKey: ["manager-branding-assets"] });
  };

  const updateAsset = useMutation({
    mutationFn: (payload: { id: string; alt_text: string }) => apiPatch(`/v3/branding/assets/${payload.id}`, { alt_text: payload.alt_text }),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ["manager-branding-assets"] }),
  });
  const deleteAsset = async (assetId: string) => {
    await apiDelete(`/v3/branding/assets/${assetId}`);
    setSnapshot((prev: any) => ({
      ...prev,
      theme: {
        ...prev.theme,
        components: {
          ...prev.theme.components,
          header: {
            ...prev.theme.components.header,
            heroImageAssetId: prev.theme.components.header.heroImageAssetId === assetId ? undefined : prev.theme.components.header.heroImageAssetId,
            auxiliaryImageAssetIds: (prev.theme.components.header.auxiliaryImageAssetIds || []).filter((x: string) => x !== assetId),
          },
        },
      },
    }));
    await qc.invalidateQueries({ queryKey: ["manager-branding-assets"] });
  };

  const visibleSections = snapshot.layout.sections.filter((x: any) => x.enabled);
  const previewSnapshot =
    previewMode === "published" && branding.data?.published?.snapshot?.theme ? branding.data.published.snapshot : snapshot;
  const dirty = baseline ? JSON.stringify(snapshot) !== baseline : false;
  const colors = previewSnapshot.theme.tokens.colors;
  const missingAlt = (assets.data?.items ?? []).filter((x) => !x.is_decorative && !x.alt_text).length;
  const textOnPrimary = contrastRatio(colors.textPrimary, colors.primary);
  const textOnSurface = contrastRatio(colors.textPrimary, colors.surface);
  const warnings = [
    ...(textOnPrimary < 4.5 ? [t("manager.branding.warning.contrastPrimary")] : []),
    ...(textOnSurface < 4.5 ? [t("manager.branding.warning.contrastSurface")] : []),
    ...(missingAlt > 0 ? [t("manager.branding.warning.missingAlt", { count: missingAlt })] : []),
  ];

  const currentSection = snapshot.layout.sections.find((x: any) => x.id === selectedSectionId) ?? snapshot.layout.sections[0];
  const currentSectionSchema = BLOCK_SCHEMAS[currentSection?.type || ""] || null;
  const currentSectionVariantOptions = SECTION_VARIANTS[currentSection?.type || ""] || [];
  const defaultLocale = activeProject?.default_locale || "en";
  const snapshotLocales = Array.isArray(snapshot?.content?.i18n?.locales) ? snapshot.content.i18n.locales.map((x: unknown) => String(x)) : [];
  const projectLocales = Array.isArray((activeProject as any)?.supported_locales) ? (activeProject as any).supported_locales.map((x: unknown) => String(x)) : [];
  const contentLocales = Array.from(new Set([defaultLocale, ...projectLocales, ...snapshotLocales])).filter(Boolean);
  const mainHeroAssetId = snapshot.theme?.components?.header?.heroImageAssetId || "";
  const auxHeroAssetIds: string[] = Array.isArray(snapshot.theme?.components?.header?.auxiliaryImageAssetIds)
    ? snapshot.theme.components.header.auxiliaryImageAssetIds
    : [];
  const serviceCard = snapshot.theme?.components?.serviceCard || {};
  const staffSelector = snapshot.theme?.components?.staffSelector || {};
  const slotChip = snapshot.theme?.components?.slotChip || {};
  const stepper = snapshot.theme?.components?.stepper || {};
  const form = snapshot.theme?.components?.form || {};
  const header = snapshot.theme?.components?.header || {};
  const setHeaderStyle = (patch: Record<string, unknown>) =>
    commitSnapshot((prev: any) => ({
      ...prev,
      theme: {
        ...prev.theme,
        components: {
          ...prev.theme.components,
          header: { ...(prev.theme.components?.header || {}), ...patch },
        },
      },
    }));
  const setComponentStyle = (key: string, patch: Record<string, unknown>) =>
    commitSnapshot((prev: any) => ({
      ...prev,
      theme: {
        ...prev.theme,
        components: {
          ...prev.theme.components,
          [key]: { ...(prev.theme.components?.[key] || {}), ...patch },
        },
      },
    }));
  const toggleAuxHeroAsset = (assetId: string) => {
    const current: string[] = Array.isArray(snapshot.theme?.components?.header?.auxiliaryImageAssetIds)
      ? snapshot.theme.components.header.auxiliaryImageAssetIds
      : [];
    const next = current.includes(assetId) ? current.filter((x) => x !== assetId) : [...current, assetId];
    setHeaderStyle({ auxiliaryImageAssetIds: next });
  };
  const setColor = (key: string, value: string) =>
    commitSnapshot((prev: any) => ({ ...prev, theme: { ...prev.theme, tokens: { ...prev.theme.tokens, colors: { ...prev.theme.tokens.colors, [key]: value } } } }));
  const reorderSectionByTarget = (dragId: string, targetId: string) => {
    if (!dragId || !targetId || dragId === targetId) return;
    commitSnapshot((prev: any) => {
      const list = [...prev.layout.sections];
      const from = list.findIndex((x: any) => x.id === dragId);
      const to = list.findIndex((x: any) => x.id === targetId);
      if (from < 0 || to < 0 || from === to) return prev;
      const [item] = list.splice(from, 1);
      list.splice(to, 0, item);
      return { ...prev, layout: { ...prev.layout, sections: list } };
    });
  };
  const setLocalizedField = (key: "header.title" | "header.subtitle" | "policy.text" | "footer.text", value: string) =>
    commitSnapshot((prev: any) => {
      const defaultLocale = activeProject?.default_locale || "en";
      const nextContent = upsertLocalizedContent(prev, key, contentLocale || defaultLocale, value, defaultLocale);
      return { ...prev, content: nextContent };
    });
  const moveSection = (id: string, dir: -1 | 1) => {
    commitSnapshot((prev: any) => {
      const list = [...prev.layout.sections];
      const idx = list.findIndex((x: any) => x.id === id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= list.length) return prev;
      [list[idx], list[j]] = [list[j], list[idx]];
      return { ...prev, layout: { ...prev.layout, sections: list } };
    });
  };
  const addBlock = (afterId?: string) => {
    commitSnapshot((prev: any) => {
      const sectionType = newBlockType || "announcement";
      const schema = BLOCK_SCHEMAS[sectionType] || BLOCK_SCHEMAS.announcement;
      const nextSection = { id: `${sectionType}-${Date.now()}`, type: sectionType, enabled: true, variant: schema?.defaultVariant || schema?.variants?.[0] || "default" };
      const list = [...prev.layout.sections];
      const idx = afterId ? list.findIndex((x: any) => x.id === afterId) : -1;
      if (idx >= 0) list.splice(idx + 1, 0, nextSection);
      else list.push(nextSection);
      return { ...prev, layout: { ...prev.layout, sections: list } };
    });
  };
  const duplicateBlock = (id: string) => {
    commitSnapshot((prev: any) => {
      const list = [...prev.layout.sections];
      const idx = list.findIndex((x: any) => x.id === id);
      if (idx < 0) return prev;
      const src = list[idx];
      const copy = { ...src, id: `${src.type}-${Date.now()}` };
      list.splice(idx + 1, 0, copy);
      return { ...prev, layout: { ...prev.layout, sections: list } };
    });
  };
  const deleteBlock = (id: string) => {
    commitSnapshot((prev: any) => {
      const target = prev.layout.sections.find((x: any) => x.id === id);
      if (!target || CORE_SECTION_TYPES.has(target.type)) return prev;
      const list = prev.layout.sections.filter((x: any) => x.id !== id);
      return { ...prev, layout: { ...prev.layout, sections: list } };
    });
  };
  const toggleBlockMobileVisibility = (id: string) => {
    commitSnapshot((prev: any) => {
      const list = prev.layout.sections.map((x: any) =>
        x.id === id ? { ...x, hiddenOnMobile: !x.hiddenOnMobile } : x
      );
      return { ...prev, layout: { ...prev.layout, sections: list } };
    });
  };
  const undo = () => {
    if (historyIndex <= 0) return;
    const nextIdx = historyIndex - 1;
    setHistoryIndex(nextIdx);
    setSnapshot(clone(historyStack[nextIdx]));
  };
  const redo = () => {
    if (historyIndex < 0 || historyIndex >= historyStack.length - 1) return;
    const nextIdx = historyIndex + 1;
    setHistoryIndex(nextIdx);
    setSnapshot(clone(historyStack[nextIdx]));
  };
  const createPreviewLink = async () => {
    const revisionForPreview =
      previewMode === "draft" ? draftRevisionId : publishedRevisionId;
    if (!revisionForPreview) return;
    try {
      if (previewMode === "draft" && baseline && JSON.stringify(snapshot) !== baseline) {
        await persistDraftSnapshot();
        setBaseline(JSON.stringify(snapshot));
      }
      const res = await apiPost<BrandingPreviewLink>(
        `/v3/branding/revisions/${encodeURIComponent(revisionForPreview)}/preview-link`,
        {}
      );
      setPreviewToken(res.preview_token);
      setPreviewUrl(res.preview_url);
      try {
        await navigator.clipboard?.writeText(`${window.location.origin}${res.preview_url}`);
      } catch {
        // best effort only
      }
    } catch {
      const token = Math.random().toString(36).slice(2, 10);
      setPreviewToken(token);
      setPreviewUrl(`/book/preview?brandingPreview=${token}`);
    }
  };

  const previewStyle = useMemo(
    () =>
      ({
        "--client-primary": colors.primary,
        "--client-accent": colors.accent,
        background: colors.background,
        color: colors.textPrimary,
        borderColor: colors.border,
        fontFamily: previewSnapshot.theme.tokens.typography.fontBody.family,
        fontSize: `${previewSnapshot.theme.tokens.typography.baseSizePx}px`,
      }) as CSSProperties,
    [colors, previewSnapshot]
  );

  return (
    <div className="grid gap-4">
      <div className="panel p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t("manager.branding.title")}</h1>
            <p className="text-sm text-slate-600">
              {projects.data?.items?.[0]?.name || "Default Brand"} | {dirty ? t("manager.branding.unsaved") : t("manager.branding.saved")}
            </p>
            <p className="text-xs text-slate-500">
              {lockState?.lock_token ? `Edit lock active until ${new Date(lockState.expires_at).toLocaleTimeString()}` : "Acquiring edit lock..."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select className="input w-full sm:w-auto" value={device} onChange={(e) => setDevice(e.target.value as "desktop" | "tablet" | "mobile")}>
              <option value="desktop">Desktop</option>
              <option value="tablet">Tablet</option>
              <option value="mobile">Mobile</option>
            </select>
            <select className="input w-full sm:w-auto" value={previewMode} onChange={(e) => setPreviewMode(e.target.value as "draft" | "published")}>
              <option value="draft">{t("manager.branding.previewMode.draft")}</option>
              <option value="published">{t("manager.branding.previewMode.published")}</option>
            </select>
            <button className="btn secondary w-full sm:w-auto" disabled={saveDraft.isPending} onClick={() => saveDraft.mutate()}>
              {t("manager.branding.action.saveDraft")}
            </button>
            <button className="btn w-full sm:w-auto" disabled={publish.isPending || saveDraft.isPending} onClick={() => publish.mutate()}>
              {t("manager.branding.action.publish")}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)_360px]">
        <aside className="panel p-4 space-y-4">
          <div>
            <h3 className="font-semibold">Editor</h3>
            <p className="mt-1 text-xs text-slate-500">Pick a section, then edit it on the right. Keep changes small and publish often.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {SIMPLE_TABS.map((item) => (
              <button
                key={item.id}
                className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${panel === item.id ? "bg-brand-500 text-white border-brand-500" : "bg-white border-slate-200"}`}
                onClick={() => setPanel(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <h3 className="font-semibold text-sm">Page</h3>
            <select className="input mt-2" value={selectedPageId} onChange={(e) => setSelectedPageId(e.target.value)}>
              <option value="booking">Booking</option>
              <option value="thank_you">Thank You</option>
            </select>
          </div>
          <div>
            <h3 className="font-semibold">{t("manager.branding.pageSections")}</h3>
            <div className="mt-2 grid grid-cols-[1fr_auto] gap-2">
              <select className="input" value={newBlockType} onChange={(e) => setNewBlockType(e.target.value)}>
                {Object.entries(BLOCK_SCHEMAS)
                  .filter(([key]) => !CORE_SECTION_TYPES.has(key))
                  .map(([key, schema]) => (
                    <option key={key} value={key}>
                      {schema.label}
                    </option>
                  ))}
              </select>
              <button className="btn secondary" onClick={() => addBlock()}>
                Add
              </button>
            </div>
            <div className="mt-2 grid gap-2">
              {snapshot.layout.sections.map((section: any) => (
                <div
                  key={section.id}
                  draggable
                  onDragStart={() => setDraggingSectionId(section.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (draggingSectionId) reorderSectionByTarget(draggingSectionId, section.id);
                    setDraggingSectionId(null);
                  }}
                  onDragEnd={() => setDraggingSectionId(null)}
                  className={`border rounded-lg p-2 ${selectedSectionId === section.id ? "border-brand-500" : "border-slate-200"} ${draggingSectionId === section.id ? "opacity-60" : ""}`}
                >
                  <button className="text-left w-full font-medium" onClick={() => setSelectedSectionId(section.id)}>
                    <span className="inline-flex items-center gap-2">
                      <span className="cursor-grab text-slate-400" aria-hidden="true">::</span>
                      <span>{BLOCK_SCHEMAS[section.type]?.label || section.type}</span>
                    </span>
                  </button>
                  <p className="mt-1 text-xs text-slate-500">{section.variant || "default"}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!section.enabled}
                      disabled={CORE_SECTION_TYPES.has(section.type)}
                      onChange={(e) =>
                        commitSnapshot((prev: any) => ({
                          ...prev,
                          layout: { ...prev.layout, sections: prev.layout.sections.map((x: any) => (x.id === section.id ? { ...x, enabled: e.target.checked } : x)) },
                        }))
                      }
                    />
                    <button className="btn secondary w-full sm:w-auto" onClick={() => moveSection(section.id, -1)}>{t("manager.branding.action.up")}</button>
                    <button className="btn secondary w-full sm:w-auto" onClick={() => moveSection(section.id, 1)}>{t("manager.branding.action.down")}</button>
                    <button className="btn secondary w-full sm:w-auto" onClick={() => duplicateBlock(section.id)}>Duplicate</button>
                    <button className="btn secondary w-full sm:w-auto" onClick={() => toggleBlockMobileVisibility(section.id)}>
                      {section.hiddenOnMobile ? "Show Mobile" : "Hide Mobile"}
                    </button>
                    {!CORE_SECTION_TYPES.has(section.type) ? (
                      <button className="btn secondary w-full sm:w-auto" onClick={() => deleteBlock(section.id)}>
                        Delete
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 p-3">
            <h3 className="font-semibold text-sm">Quick Actions</h3>
            <div className="mt-2 grid gap-2">
              <button className="btn secondary" onClick={undo} disabled={historyIndex <= 0}>Undo</button>
              <button className="btn secondary" onClick={redo} disabled={historyIndex < 0 || historyIndex >= historyStack.length - 1}>Redo</button>
              <button className="btn secondary" onClick={() => void createPreviewLink()}>Copy Preview Link</button>
            </div>
            {previewToken ? <p className="mt-2 text-xs text-slate-500">Preview token: <code>{previewToken}</code></p> : null}
            {previewUrl ? <p className="mt-1 text-xs text-slate-500 break-all">URL: <code>{previewUrl}</code></p> : null}
          </div>
        </aside>

        <section className="panel p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-medium">Canvas • {selectedPageId === "booking" ? "Booking Funnel" : "Thank You"}</div>
            <div className="flex flex-wrap gap-2">
              <select className="input" value={newBlockType} onChange={(e) => setNewBlockType(e.target.value)}>
                {Object.entries(BLOCK_SCHEMAS)
                  .filter(([key]) => !CORE_SECTION_TYPES.has(key))
                  .map(([key, schema]) => (
                    <option key={key} value={key}>
                      {schema.label}
                    </option>
                  ))}
              </select>
              <button className="btn secondary" onClick={() => addBlock()}>Add Block</button>
            </div>
          </div>
          <div className={`mx-auto border rounded-2xl p-4 ${device === "mobile" ? "max-w-[390px]" : device === "tablet" ? "max-w-[760px]" : "max-w-full"}`} style={previewStyle}>
            <header
              className={`rounded-xl p-4 text-white ${selectedSectionId === "header" ? "ring-2 ring-white/90" : ""}`}
              style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
              onClick={() => setSelectedSectionId("header")}
            >
              <p className="text-xs opacity-80">Main photo: {mainHeroAssetId ? mainHeroAssetId.slice(0, 8) : "none"} | Aux: {auxHeroAssetIds.length}</p>
              {previewMode === "draft" ? (
                <>
                  <input
                    className="mt-1 w-full rounded bg-white/10 px-2 py-1 text-2xl font-semibold outline-none placeholder:text-white/70"
                    style={{ fontFamily: previewSnapshot.theme.tokens.typography.fontHeading.family }}
                    value={getLocalizedContentValue(snapshot, "header.title", contentLocale, defaultLocale)}
                    onChange={(e) => setLocalizedField("header.title", e.target.value)}
                    placeholder="Hero title"
                  />
                  <textarea
                    className="mt-2 w-full rounded bg-white/10 px-2 py-1 text-sm opacity-95 outline-none placeholder:text-white/70"
                    rows={2}
                    value={getLocalizedContentValue(snapshot, "header.subtitle", contentLocale, defaultLocale)}
                    onChange={(e) => setLocalizedField("header.subtitle", e.target.value)}
                    placeholder="Hero subtitle"
                  />
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-semibold" style={{ fontFamily: previewSnapshot.theme.tokens.typography.fontHeading.family }}>
                    {previewSnapshot.content.header.title}
                  </h2>
                  <p className="opacity-90">{previewSnapshot.content.header.subtitle}</p>
                </>
              )}
            </header>
            <div className="mt-4 grid gap-3">
              <button className="w-full rounded border border-dashed border-slate-300 px-3 py-2 text-left text-xs text-slate-500 hover:border-brand-300" onClick={() => addBlock()}>
                + Add block
              </button>
              {visibleSections.map((section: any) => (
                <div
                  key={section.id}
                  draggable
                  onDragStart={() => setDraggingSectionId(section.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (draggingSectionId) reorderSectionByTarget(draggingSectionId, section.id);
                    setDraggingSectionId(null);
                  }}
                  onDragEnd={() => setDraggingSectionId(null)}
                  className={`rounded-lg border p-3 ${selectedSectionId === section.id ? "ring-2 ring-brand-400" : ""}`}
                  style={{ borderColor: colors.border, background: colors.surface, opacity: section.hiddenOnMobile && device === "mobile" ? 0.45 : 1 }}
                  onClick={() => setSelectedSectionId(section.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm uppercase tracking-wide text-slate-500 flex items-center gap-2">
                      <span className="text-slate-400">::</span>
                      <span>{BLOCK_SCHEMAS[section.type]?.label || section.type}</span>
                    </div>
                    <div className="text-xs text-slate-500">{section.variant || "default"}</div>
                  </div>
                  <div className="mt-2 text-sm text-slate-700">
                    {section.type === "policies" && previewMode === "draft" ? (
                      <textarea
                        className="input"
                        rows={3}
                        value={getLocalizedContentValue(snapshot, "policy.text", contentLocale, defaultLocale)}
                        onChange={(e) => setLocalizedField("policy.text", e.target.value)}
                      />
                    ) : section.type === "policies" ? (
                      getLocalizedContentValue(previewSnapshot, "policy.text", contentLocale, defaultLocale) || previewSnapshot.content.policies.beforeYouArrive
                    ) : (
                      `Interactive preview block (${section.variant || "default"})`
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <aside className="panel p-4 space-y-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            Simple mode: style, content, media, publish. Advanced/legacy controls are hidden.
          </div>

          {panel === "brandKit" && (
            <>
              <h3 className="font-semibold">{t("manager.branding.tokens")}</h3>
              {templates.data?.items?.length ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                  Template quick apply:
                  <div className="mt-2 flex flex-wrap gap-2">
                    {templates.data.items.slice(0, 3).map((tpl) => (
                      <button key={tpl.id} className="rounded border border-slate-300 bg-white px-2 py-1" onClick={() => applyTemplate(tpl.id)}>
                        {tpl.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              {(["primary", "secondary", "accent", "background", "surface", "textPrimary", "textSecondary", "border"] as const).map((key) => (
                <label key={key} className="block text-sm">
                  {key}
                  <input className="input mt-1" value={snapshot.theme.tokens.colors[key]} onChange={(e) => setColor(key, e.target.value)} />
                </label>
              ))}
              <label className="block text-sm">
                {t("manager.branding.baseSize")}
                <input
                  className="input mt-1"
                  type="number"
                  min={14}
                  max={18}
                  value={snapshot.theme.tokens.typography.baseSizePx}
                  onChange={(e) =>
                    commitSnapshot((prev: any) => ({ ...prev, theme: { ...prev.theme, tokens: { ...prev.theme.tokens, typography: { ...prev.theme.tokens.typography, baseSizePx: Number(e.target.value) || 16 } } } }))
                  }
                />
              </label>
            </>
          )}

          {(panel === "layout" || panel === "brandKit") && (
            <>
              <h3 className="font-semibold">{panel === "brandKit" ? "Layout & Components" : t("manager.branding.sectionInspector")}</h3>
              <p className="text-sm text-slate-600">{currentSectionSchema?.label || currentSection?.type || t("manager.branding.noSectionSelected")}</p>
              <label className="text-xs font-medium text-slate-500">{t("manager.branding.sectionVariant")}</label>
              <select
                className="input"
                value={currentSection?.variant || ""}
                onChange={(e) =>
                  commitSnapshot((prev: any) => ({
                    ...prev,
                    layout: { ...prev.layout, sections: prev.layout.sections.map((x: any) => (x.id === currentSection.id ? { ...x, variant: e.target.value } : x)) },
                  }))
                }
              >
                <option value="">{t("manager.branding.default")}</option>
                {currentSectionVariantOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>

              <h4 className="font-semibold mt-3">{t("manager.branding.header")}</h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <select className="input" value={header.alignment || "left"} onChange={(e) => setHeaderStyle({ alignment: e.target.value })}>
                  <option value="left">{t("manager.branding.alignLeft")}</option>
                  <option value="center">{t("manager.branding.alignCenter")}</option>
                </select>
                <select className="input" value={header.paddingY || "lg"} onChange={(e) => setHeaderStyle({ paddingY: e.target.value })}>
                  <option value="sm">{t("manager.branding.compactPadding")}</option>
                  <option value="md">{t("manager.branding.mediumPadding")}</option>
                  <option value="lg">{t("manager.branding.largePadding")}</option>
                </select>
                <input
                  className="input"
                  value={header.overlay?.color || "#000000"}
                  onChange={(e) =>
                    setHeaderStyle({ overlay: { ...(header.overlay || { opacity: 0.35 }), color: e.target.value } })
                  }
                />
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={typeof header.overlay?.opacity === "number" ? header.overlay.opacity : 0.35}
                  onChange={(e) =>
                    setHeaderStyle({
                      overlay: {
                        ...(header.overlay || { color: "#000000" }),
                        opacity: Number(e.target.value),
                      },
                    })
                  }
                />
              </div>

              <h4 className="font-semibold mt-3">{t("services")}</h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <select className="input" value={serviceCard.variant || "grid"} onChange={(e) => setComponentStyle("serviceCard", { variant: e.target.value })}>
                  <option value="list">{t("manager.branding.list")}</option>
                  <option value="grid">{t("manager.branding.grid")}</option>
                  <option value="cardsWithImages">{t("manager.branding.cardsWithImages")}</option>
                </select>
                <select className="input" value={serviceCard.imageMode || "cover"} onChange={(e) => setComponentStyle("serviceCard", { imageMode: e.target.value })}>
                  <option value="cover">{t("manager.branding.imageCover")}</option>
                  <option value="contain">{t("manager.branding.imageContain")}</option>
                </select>
                <label className="text-sm flex items-center gap-2">
                  <input type="checkbox" checked={serviceCard.showDurationBadge ?? true} onChange={(e) => setComponentStyle("serviceCard", { showDurationBadge: e.target.checked })} />
                  {t("manager.branding.showDuration")}
                </label>
                <label className="text-sm flex items-center gap-2">
                  <input type="checkbox" checked={serviceCard.showPrice ?? true} onChange={(e) => setComponentStyle("serviceCard", { showPrice: e.target.checked })} />
                  {t("manager.branding.showPrice")}
                </label>
              </div>

              <h4 className="font-semibold mt-3">{t("staff")}</h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <select className="input" value={staffSelector.variant || "pills"} onChange={(e) => setComponentStyle("staffSelector", { variant: e.target.value })}>
                  <option value="pills">{t("manager.branding.pills")}</option>
                  <option value="cardsWithPhotos">{t("manager.branding.cardsWithPhotos")}</option>
                </select>
                <label className="text-sm flex items-center gap-2">
                  <input type="checkbox" checked={staffSelector.showPhotos ?? false} onChange={(e) => setComponentStyle("staffSelector", { showPhotos: e.target.checked })} />
                  {t("manager.branding.showPhotos")}
                </label>
              </div>

              <h4 className="font-semibold mt-3">{t("manager.branding.timeSlots")}</h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <select className="input" value={slotChip.variant || "outline"} onChange={(e) => setComponentStyle("slotChip", { variant: e.target.value })}>
                  <option value="outline">{t("manager.branding.outline")}</option>
                  <option value="filled">{t("manager.branding.filled")}</option>
                </select>
                <select className="input" value={slotChip.size || "md"} onChange={(e) => setComponentStyle("slotChip", { size: e.target.value })}>
                  <option value="sm">{t("manager.branding.small")}</option>
                  <option value="md">{t("manager.branding.medium")}</option>
                </select>
              </div>

              <h4 className="font-semibold mt-3">{t("manager.branding.stepper")}</h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <select className="input" value={stepper.variant || "numbers"} onChange={(e) => setComponentStyle("stepper", { variant: e.target.value })}>
                  <option value="numbers">{t("manager.branding.numbers")}</option>
                  <option value="dots">{t("manager.branding.dots")}</option>
                </select>
                <label className="text-sm flex items-center gap-2">
                  <input type="checkbox" checked={stepper.showLabels ?? true} onChange={(e) => setComponentStyle("stepper", { showLabels: e.target.checked })} />
                  {t("manager.branding.showLabels")}
                </label>
              </div>

              <h4 className="font-semibold mt-3">{t("manager.branding.form")}</h4>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <select className="input" value={form.inputVariant || "outline"} onChange={(e) => setComponentStyle("form", { inputVariant: e.target.value })}>
                  <option value="outline">{t("manager.branding.outline")}</option>
                  <option value="filled">{t("manager.branding.filled")}</option>
                </select>
                <select className="input" value={form.radius || "md"} onChange={(e) => setComponentStyle("form", { radius: e.target.value })}>
                  <option value="sm">{t("manager.branding.smallRadius")}</option>
                  <option value="md">{t("manager.branding.mediumRadius")}</option>
                  <option value="lg">{t("manager.branding.largeRadius")}</option>
                </select>
              </div>
            </>
                          )}

          {panel === "media" && (
            <>
              <h3 className="font-semibold">{t("manager.branding.mediaLibrary")}</h3>
              <p className="text-xs text-slate-600">{t("manager.branding.heroMain", { id: mainHeroAssetId || t("manager.settings.notSet"), count: auxHeroAssetIds.length })}</p>
              <p className="text-xs text-slate-500">{t("manager.branding.mediaHint")}</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <select className="input" value={assetFilter} onChange={(e) => setAssetFilter(e.target.value)}>
                  <option value="all">{t("manager.branding.filter.all")}</option>
                  <option value="hero">{t("manager.branding.filter.hero")}</option>
                  <option value="logo">{t("manager.branding.filter.logo")}</option>
                  <option value="service">{t("manager.branding.filter.service")}</option>
                  <option value="staff">{t("manager.branding.filter.staff")}</option>
                </select>
                <input className="input" placeholder={t("manager.branding.search")} value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <input className="input" type="file" onChange={(e) => e.target.files?.[0] && uploadAsset(e.target.files[0], assetFilter === "all" ? "hero" : assetFilter)} />
              <div className="grid gap-2 max-h-[420px] overflow-auto">
                {(assets.data?.items ?? []).map((asset) => (
                  <div key={asset.id} className="border rounded-lg p-2">
                    <AssetPreview assetId={asset.id} token={token} className="h-24 w-full rounded object-cover border bg-slate-100" alt={asset.title || asset.original_filename} />
                    <div className="text-sm font-medium">{asset.title || asset.original_filename}</div>
                    <div className="text-xs text-slate-500">{asset.type}</div>
                    <input className="input mt-1" placeholder={t("manager.branding.altText")} defaultValue={asset.alt_text || ""} onBlur={(e) => updateAsset.mutate({ id: asset.id, alt_text: e.target.value })} />
                    <button
                      className="btn secondary mt-2"
                      onClick={() => setHeaderStyle({ heroImageAssetId: asset.id })}
                    >
                      {mainHeroAssetId === asset.id ? t("manager.branding.mainHeroSelected") : t("manager.branding.setMainHero")}
                    </button>
                    <button className="btn secondary mt-2" onClick={() => toggleAuxHeroAsset(asset.id)}>
                      {auxHeroAssetIds.includes(asset.id) ? t("manager.branding.removeAux") : t("manager.branding.addAux")}
                    </button>
                    <button className="btn secondary mt-2" onClick={() => deleteAsset(asset.id)}>
                      {t("common.delete")}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {panel === "content" && (
            <>
              <h3 className="font-semibold">{t("manager.branding.publicContent")}</h3>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <select className="input" value={contentLocale} onChange={(e) => setContentLocale(e.target.value)}>
                  {contentLocales.map((locale) => (
                    <option key={locale} value={locale}>
                      {locale}
                    </option>
                  ))}
                </select>
                <button
                  className="btn secondary"
                  onClick={() => {
                    const next = prompt("Add locale code (e.g. es, ru, ar)");
                    if (!next) return;
                    const normalized = next.trim().toLowerCase();
                    if (!normalized) return;
                    setContentLocale(normalized);
                    commitSnapshot((prev: any) => ({
                      ...prev,
                      content: {
                        ...(prev.content || {}),
                        i18n: {
                          ...((prev.content || {}).i18n || {}),
                          locales: Array.from(new Set([...(Array.isArray((prev.content || {}).i18n?.locales) ? (prev.content || {}).i18n.locales : []), normalized, defaultLocale])),
                          strings: { ...(((prev.content || {}).i18n || {}).strings || {}) },
                        },
                      },
                    }));
                  }}
                >
                  + Locale
                </button>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                Editing locale `{contentLocale}` (default `{defaultLocale}`). Default locale updates the live fallback fields; non-default locales are stored in `content.i18n.strings`.
              </div>
              <input
                className="input"
                value={getLocalizedContentValue(snapshot, "header.title", contentLocale, defaultLocale)}
                onChange={(e) => setLocalizedField("header.title", e.target.value)}
                placeholder={t("manager.branding.placeholder.headerTitle")}
              />
              <textarea
                className="input"
                rows={3}
                value={getLocalizedContentValue(snapshot, "header.subtitle", contentLocale, defaultLocale)}
                onChange={(e) => setLocalizedField("header.subtitle", e.target.value)}
                placeholder={t("manager.branding.placeholder.headerSubtitle")}
              />
              <textarea
                className="input"
                rows={4}
                value={getLocalizedContentValue(snapshot, "policy.text", contentLocale, defaultLocale)}
                onChange={(e) => setLocalizedField("policy.text", e.target.value)}
                placeholder={t("manager.branding.placeholder.beforeYouArrive")}
              />
              <textarea
                className="input"
                rows={2}
                value={getLocalizedContentValue(snapshot, "footer.text", contentLocale, defaultLocale)}
                onChange={(e) => setLocalizedField("footer.text", e.target.value)}
                placeholder="Footer text"
              />
              {currentSectionSchema?.contentFields?.length ? (
                <div className="rounded-lg border border-slate-200 p-2 text-xs text-slate-700">
                  <p className="font-semibold mb-2">Selected block content schema: {currentSectionSchema.label}</p>
                  <div className="grid gap-2">
                    {currentSectionSchema.contentFields.map((field) =>
                      field.multiline ? (
                        <textarea
                          key={field.key}
                          className="input"
                          rows={3}
                          value={getLocalizedContentValue(snapshot, field.key, contentLocale, defaultLocale)}
                          onChange={(e) => setLocalizedField(field.key, e.target.value)}
                          placeholder={field.label}
                        />
                      ) : (
                        <input
                          key={field.key}
                          className="input"
                          value={getLocalizedContentValue(snapshot, field.key, contentLocale, defaultLocale)}
                          onChange={(e) => setLocalizedField(field.key, e.target.value)}
                          placeholder={field.label}
                        />
                      )
                    )}
                  </div>
                </div>
              ) : null}
              <div className="rounded-lg border border-slate-200 p-2 text-xs text-slate-600">
                Localization and block schemas are persisted in the same revision snapshot and published through the v2 compiled theme payload.
              </div>
            </>
          )}

          {panel === "publish" && (
            <>
              <h3 className="font-semibold">{t("manager.branding.versionHistory")}</h3>
              <textarea className="input" rows={2} value={publishNotes} onChange={(e) => setPublishNotes(e.target.value)} placeholder={t("manager.branding.placeholder.publishNotes")} />
              <div className="grid gap-2 max-h-[360px] overflow-auto">
                {(versions.data?.items ?? []).map((v) => (
                  <div key={v.version} className="border rounded-lg p-2">
                    <div className="text-sm font-medium">v{v.version}</div>
                    <div className="text-xs text-slate-500">{new Date(v.created_at).toLocaleString()}</div>
                    <div className="text-xs text-slate-500">{v.notes || t("manager.branding.noNotes")}</div>
                    <button className="btn secondary mt-2" onClick={() => rollback.mutate(v.version)}>
                      {t("manager.branding.action.rollbackToDraft")}
                    </button>
                  </div>
                ))}
              </div>
              {versions.data?.items?.[0]?.snapshot ? (
                <div className="rounded-lg border border-slate-200 p-2 text-xs text-slate-600">
                  Diff vs latest published:{" "}
                  {v2Diff.data?.summary?.changed_count !== undefined
                    ? `${v2Diff.data.summary.changed_count} changed path(s)`
                    : (() => {
                        const current = JSON.stringify(snapshot);
                        const last = JSON.stringify((versions.data?.items?.[0] as any)?.snapshot || {});
                        return current === last ? "No changes" : `${Math.abs(current.length - last.length)} bytes changed (approx)`;
                      })()}
                </div>
              ) : null}
              {serverValidation ? (
                <div className="rounded-lg border border-slate-200 p-2 text-xs text-slate-700">
                  <p className="font-semibold">{serverValidation.ok ? "Server validation: OK" : "Server validation failed"}</p>
                  {serverValidation.errors?.slice(0, 4).map((issue) => (
                    <p key={`e-${issue.path}-${issue.code}`} className="text-red-700">{issue.message}</p>
                  ))}
                  {serverValidation.warnings?.slice(0, 4).map((issue) => (
                    <p key={`w-${issue.path}-${issue.code}`} className="text-amber-700">{issue.message}</p>
                  ))}
                </div>
              ) : null}
            </>
          )}

          {panel === "advanced" && (
            <>
              <h3 className="font-semibold">{t("manager.branding.accessibilitySafety")}</h3>
              {warnings.length === 0 ? <p className="text-sm text-emerald-700">{t("manager.branding.noWarnings")}</p> : warnings.map((w) => <p key={w} className="text-sm text-amber-700">{w}</p>)}
              <div className="text-xs text-slate-500">
                <p>{t("manager.branding.publishedVersion", { version: branding.data?.published?.version ?? t("manager.staff.none") })}</p>
                <p>{t("manager.branding.draftUpdated", { value: branding.data?.draft?.updated_at ? new Date(branding.data.draft.updated_at).toLocaleString() : "n/a" })}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                <p className="font-semibold text-slate-700">V2 API</p>
                <p>Projects: `/v3/branding/projects`</p>
                <p>Templates: `/v3/branding/templates`</p>
                <p>Draft revision: `/v3/branding/revisions/draft:{branding.data?.tenant_id}:default`</p>
                <p>Draft etag: `{draftEtag || "n/a"}`</p>
              </div>
              {serverValidation?.errors?.length ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                  {serverValidation.errors.slice(0, 5).map((issue) => (
                    <p key={`${issue.code}-${issue.path}`}>{issue.path}: {issue.message}</p>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </aside>
      </div>

      {saveDraft.isError ? <p className="text-red-600 text-sm">{String(saveDraft.error)}</p> : null}
      {publish.isError ? <p className="text-red-600 text-sm">{String(publish.error)}</p> : null}
      {rollback.isError ? <p className="text-red-600 text-sm">{String(rollback.error)}</p> : null}
    </div>
  );
}

